import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from './PageSEO';

const STATS = [
  { value: '37',    label: 'Free Games & Solvers' },
  { value: '190K+', label: 'Word Dictionary'       },
  { value: '100%',  label: 'Free, No Sign-up'      },
  { value: '0',     label: 'Downloads Needed'      },
];

const FEATURES = [
  {
    icon: '⚡',
    title: 'Instant Results',
    desc: 'Every solver and tool runs entirely in your browser — no waiting for a server, no loading spinners. Results appear as you type.',
  },
  {
    icon: '📱',
    title: 'Works on Any Device',
    desc: 'Fully responsive design that feels great on a phone, tablet, or desktop. Play on the couch or at your desk.',
  },
  {
    icon: '🔒',
    title: 'No Account Required',
    desc: 'Jump straight into any game. No account, no email, no password required. Just open the page and play.',
  },
  {
    icon: '🆓',
    title: 'Always Free',
    desc: 'Every single game and solver is completely free. No premium tiers, no credits, no paywalls — ever.',
  },
  {
    icon: '📖',
    title: '190,000+ Word Dictionary',
    desc: 'Our solvers are backed by official Scrabble word lists of 190,000+ words, while our casual games use a curated list of common words to keep answers fair and familiar.',
  },
  {
    icon: '🎯',
    title: 'Built for Word Lovers',
    desc: 'From casual Wordle fans to competitive Scrabble players — every tool is designed to be genuinely useful, not just flashy.',
  },
];

const GAME_CATEGORIES = [
  {
    emoji: '🕹',
    title: 'Word Games',
    color: '#4f46e5',
    items: [
      ['Wordle', '/wordle'],
      ['Dordle', '/dordle'],
      ['Quordle', '/quordle'],
      ['Connections', '/connections'],
      ['Hangman', '/hangman'],
      ['Word Scramble', '/word-scramble'],
      ['Word Search', '/word-search'],
      ['Word Chain', '/word-chain'],
      ['Word Twist', '/word-twist'],
      ['Waffle Word Game', '/waffle'],
      ['WordBloom', '/wordbloom'],
      ['Letter Boxed', '/letter-boxed'],
    ],
  },
  {
    emoji: '🔧',
    title: 'Word Solvers',
    color: '#059669',
    items: [
      ['Jumble Solver', '/jumble'],
      ['Word Unscrambler', '/unscramble'],
      ['Anagram Solver', '/anagram-solver'],
      ['Crossword Solver', '/crossword'],
      ['Scrabble Finder', '/scrabble'],
      ['Wordfeud Helper', '/wordfeud-helper'],
      ['Boggle Solver', '/boggle'],
      ['Wordle Solver', '/wordle-solver'],
      ['Spelling Bee Solver', '/spelling-bee'],
    ],
  },
  {
    emoji: '🛠',
    title: 'Word Tools',
    color: '#d97706',
    items: [
      ['Random Word Generator', '/random-word-generator'],
      ['Word Counter', '/word-counter'],
      ['Syllable Counter', '/syllable-counter'],
      ['Rhyme Finder', '/rhyme'],
      ['Anagram Generator', '/anagram-generator'],
      ['Pig Latin Converter', '/pig-latin'],
      ['Morse Code Converter', '/morse-code'],
      ['Cryptogram Solver', '/cryptogram'],
    ],
  },
];

export default function About() {
  return (
    <div>
      <PageSEO page="about" />

      {/* ── Hero ── */}
      <section style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
        color: '#fff',
        padding: '4rem 1rem 3rem',
        textAlign: 'center',
      }}>
        <div className="container" style={{ maxWidth: 680 }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📖</div>
          <h1 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 2.8rem)', marginBottom: '1rem', letterSpacing: '-0.02em' }}>
            About <span style={{ color: '#a5b4fc' }}>Word</span><span style={{ color: '#6ee7b7' }}>Zingo</span>
          </h1>
          <p style={{ fontSize: '1.1rem', opacity: 0.88, lineHeight: 1.7, marginBottom: '2rem' }}>
            WordZingo is a free word game hub built for everyone who loves words —
            from kids discovering language to seasoned Scrabble champions.
            No sign-ups, no downloads — just great word games and solvers.
          </p>
          <Link to="/" className="btn fw-bold"
            style={{ borderRadius: 50, background: '#a5b4fc', color: '#1e1b4b', border: 'none', fontFamily: 'Nunito, sans-serif', padding: '0.65rem 2rem', fontSize: '1rem' }}>
            🎮 Explore All Games
          </Link>
        </div>
      </section>

      {/* ── Stats ── */}
      <section style={{ background: '#f8fafc', padding: '2.5rem 1rem', borderBottom: '2px solid var(--border)' }}>
        <div className="container-xl">
          <div className="row g-3 justify-content-center">
            {STATS.map(({ value, label }) => (
              <div key={label} className="col-6 col-sm-3">
                <div style={{ textAlign: 'center', background: '#fff', borderRadius: 14, padding: '1.25rem 0.5rem', border: '2px solid var(--border)' }}>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '2rem', color: 'var(--primary)', lineHeight: 1 }}>{value}</div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#64748b', marginTop: 4 }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mission ── */}
      <section style={{ padding: '3rem 1rem' }}>
        <div className="container" style={{ maxWidth: 720 }}>
          <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: '#1e293b', marginBottom: '1rem' }}>
            Our Mission
          </h2>
          <p style={{ fontSize: '1.05rem', color: '#374151', lineHeight: 1.8, marginBottom: '1rem' }}>
            We believe word games make people smarter, sharper, and happier — but only if they're
            actually accessible. Too many word game sites are cluttered with ads, buried behind
            paywalls, or frustrating to use on a phone. WordZingo was built to fix that.
          </p>
          <p style={{ fontSize: '1.05rem', color: '#374151', lineHeight: 1.8, marginBottom: '1rem' }}>
            Our mission is simple: give every word lover on the planet a clean, fast, completely
            free place to play, learn, and solve — regardless of their device, skill level, or
            budget. Every game and tool on this site will always be free. That's a promise.
          </p>
          <p style={{ fontSize: '1.05rem', color: '#374151', lineHeight: 1.8 }}>
            Whether you're squeezing in a quick Wordle on your commute, helping your child
            practise spelling, or looking for the highest-scoring Scrabble play of your life —
            WordZingo has the tool you need.
          </p>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ background: '#f8fafc', padding: '3rem 1rem', borderTop: '2px solid var(--border)', borderBottom: '2px solid var(--border)' }}>
        <div className="container-xl">
          <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: '#1e293b', textAlign: 'center', marginBottom: '0.4rem' }}>
            Why WordZingo?
          </h2>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.95rem', marginBottom: '2rem' }}>
            What makes us different from other word game sites
          </p>
          <div className="row g-3">
            {FEATURES.map(({ icon, title, desc }) => (
              <div key={title} className="col-12 col-sm-6 col-lg-4">
                <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', border: '2px solid var(--border)', height: '100%' }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>{icon}</div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1e293b', marginBottom: '0.4rem' }}>{title}</div>
                  <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Game Categories ── */}
      <section style={{ padding: '3rem 1rem' }}>
        <div className="container-xl">
          <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: '#1e293b', textAlign: 'center', marginBottom: '0.4rem' }}>
            Everything We Offer
          </h2>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.95rem', marginBottom: '2rem' }}>
            37 free games, solvers, and word tools — all in one place
          </p>
          <div className="row g-4">
            {GAME_CATEGORIES.map(({ emoji, title, color, items }) => (
              <div key={title} className="col-12 col-md-4">
                <div style={{ background: '#fff', borderRadius: 16, border: '2px solid var(--border)', overflow: 'hidden', height: '100%' }}>
                  <div style={{ background: color, padding: '0.85rem 1.25rem', color: '#fff' }}>
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '1.05rem' }}>
                      {emoji} {title}
                    </span>
                  </div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: '0.75rem 0' }}>
                    {items.map(([name, path]) => (
                      <li key={path}>
                        <Link to={path} style={{ display: 'block', padding: '0.35rem 1.25rem', fontSize: '0.88rem', fontWeight: 600, color: '#374151', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = color; }}
                          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = '#374151'; }}>
                          {name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Story ── */}
      <section style={{ background: '#f8fafc', padding: '3rem 1rem', borderTop: '2px solid var(--border)', borderBottom: '2px solid var(--border)' }}>
        <div className="container" style={{ maxWidth: 700 }}>
          <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: '#1e293b', marginBottom: '1rem' }}>
            The Story Behind WordZingo
          </h2>
          <p style={{ fontSize: '1.05rem', color: '#374151', lineHeight: 1.8, marginBottom: '1rem' }}>
            WordZingo started as a personal project — a single Wordle clone built on a weekend for
            fun. But after sharing it, the feedback kept asking: <em>"Can you add Hangman?"</em>,
            <em> "I need a Scrabble word finder"</em>, <em>"What about a Jumble solver?"</em>. One
            tool became ten, ten became twenty, and WordZingo grew into the word game hub it is today.
          </p>
          <p style={{ fontSize: '1.05rem', color: '#374151', lineHeight: 1.8, marginBottom: '1rem' }}>
            Every game and solver is built with care — tested on real phones, refined based on real
            feedback, and designed to be genuinely useful rather than just "good enough." The word
            dictionary was curated by hand to remove obscure jargon and include only natural,
            recognisable English words.
          </p>
          <p style={{ fontSize: '1.05rem', color: '#374151', lineHeight: 1.8 }}>
            We're constantly adding new games and improving existing ones. Have a suggestion or
            found a bug? We'd love to hear from you.
          </p>
          <div className="mt-3">
            <Link to="/" className="btn fw-bold"
              style={{ borderRadius: 50, background: 'var(--primary)', color: '#fff', border: 'none', fontFamily: 'Nunito, sans-serif', padding: '0.6rem 1.5rem' }}>
              🎮 Play a Game
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '3rem 1rem' }}>
        <div className="container" style={{ maxWidth: 700 }}>
          <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: '#1e293b', marginBottom: '1.5rem' }}>
            Frequently Asked Questions
          </h2>
          {[
            {
              q: 'Is WordZingo really free?',
              a: 'Yes — completely. Every game, solver, and tool is free to use with no sign-up, no subscription, and no hidden fees. WordZingo is free to use and supported by unobtrusive ads that help keep the site running.',
            },
            {
              q: 'Does WordZingo work offline?',
              a: 'Most games work fine on a fast connection. Some tools (like the word validators) call our backend API, so they require internet. We\'re exploring offline support as a future feature.',
            },
            {
              q: 'Where does the word list come from?',
              a: 'It depends on the tool. Our word solvers (Unscrambler, Scrabble Finder, Anagram, Boggle and more) use official Scrabble word lists — TWL, SOWPODS and Words With Friends, up to 267,000 words — so even obscure valid plays show up. Our casual games use a smaller, hand-curated list of common English words, with proper nouns, jargon and archaic words removed, to keep gameplay natural and fair.',
            },
            {
              q: 'How often are new games added?',
              a: 'We add new games and tools regularly. The most recent additions include Letter Boxed, WordBloom, the Waffle Word Game, Word Twist, Connections, and a multi-size Boggle Solver. Follow us or bookmark the homepage to catch new additions.',
            },
            {
              q: 'Is my data stored anywhere?',
              a: 'We don\'t collect personal data. Some features (like saved words in the Random Word Generator or Wordle streaks) store data locally in your browser\'s localStorage — it never leaves your device.',
            },
          ].map(({ q, a }) => (
            <details key={q} style={{ borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
              <summary style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1e293b', cursor: 'pointer', padding: '0.5rem 0', userSelect: 'none' }}>
                {q}
              </summary>
              <p style={{ fontSize: '0.95rem', color: '#374151', lineHeight: 1.7, margin: '0.5rem 0 0.25rem 0' }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', color: '#fff', padding: '3rem 1rem', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: 560 }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎉</div>
          <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(1.4rem, 4vw, 1.9rem)', marginBottom: '0.75rem' }}>
            Ready to Play?
          </h2>
          <p style={{ opacity: 0.88, lineHeight: 1.7, marginBottom: '1.5rem' }}>
            37 free word games and solvers are waiting. No sign-up needed — just pick a game and start!
          </p>
          <Link to="/" className="btn fw-bold"
            style={{ borderRadius: 50, background: '#a5b4fc', color: '#1e1b4b', border: 'none', fontFamily: 'Nunito, sans-serif', padding: '0.7rem 2.5rem', fontSize: '1.05rem' }}>
            🎮 Explore All 37 Games
          </Link>
        </div>
      </section>
    </div>
  );
}
